import React, { useState } from 'react';
import { FormControlLabel, Radio, RadioGroup } from '@mui/material';
import {
  ModifyRecurringMeetingTypes, TModifyRecurringType, useViewModel, zIndexCurrentTimeLine,
} from '@utils';
import { isMobile } from 'react-device-detect';
import { CalendarViewModel } from '@views';
import { observer } from 'mobx-react-lite';
import { Button } from '../button';
import { MeetingViewModel } from '../calendar';
import { MessageDialogViewModel } from '../messageDialog';
import { Portal } from '../portal';
import { ScheduleViewModel } from '../../views/calendarPage/Schedule/ScheduleViewModel';
import '../../styles/components/changeRecurringMeetingDialog.scss';

export const ChangeRecurringMeetingDialog = observer(() => {
  const messageDialogViewModel = useViewModel(MessageDialogViewModel);
  const meetingViewModel = useViewModel(MeetingViewModel);
  const calendarViewModel = useViewModel(CalendarViewModel);
  const scheduleViewModel = useViewModel(ScheduleViewModel);
  const [modifyRecurringMeetingType, setModifyRecurringMeetingType] = useState<TModifyRecurringType>(ModifyRecurringMeetingTypes.ONLY_THIS_MEETING);
  const [buttonDisabled, setButtonDisabled] = useState(false);

  const isEditOpen = !!meetingViewModel.meetingData;
  const checkedValue = isEditOpen ? meetingViewModel.meetingData.modifyRecurringMeetingType : modifyRecurringMeetingType;

  const onCheckValue = (type: TModifyRecurringType) => {
    if (isEditOpen) {
      meetingViewModel.onUpdateMeeting('modifyRecurringMeetingType', type);
    } else {
      setModifyRecurringMeetingType(type);
    }
  };

  const onDelete = () => {
    setButtonDisabled(true);
    meetingViewModel.deleteRecurringMeeting(
      isEditOpen 
      ? {
        meetingId: meetingViewModel.meetingData.id,
        remoteMeetingDate: meetingViewModel.meetingData.startDate.toISOString(),
        modifyRecurringMeetingType: meetingViewModel.meetingData.modifyRecurringMeetingType,
      }
      : {
        meetingId: meetingViewModel.moreMeetingInformation.id,
        remoteMeetingDate: meetingViewModel.moreMeetingInformation.startDate.toISOString(),
        modifyRecurringMeetingType: modifyRecurringMeetingType,
      }
    ).then(() => {
      messageDialogViewModel.closeChange();
      setButtonDisabled(false);
      if (isEditOpen) {
        meetingViewModel.resetMeetingData();
      } else {
        meetingViewModel.closeMoreMeetingInformationModal();
      }
      //TODO: Постараться удалить повторные запросы митингов и раписания
      isMobile ? scheduleViewModel.getMeetingsFromStartDay() : calendarViewModel.getAllMeetings();
    }, () => {
      setButtonDisabled(false);
    });
  };

  const onChange = () => {
    setButtonDisabled(true);
    (messageDialogViewModel.changeParams?.handleChange
      ? messageDialogViewModel.changeParams.handleChange(modifyRecurringMeetingType)
      : meetingViewModel.updateMeeting()).then((res) => {
        messageDialogViewModel.closeChange();
        setButtonDisabled(false);
        res && meetingViewModel.resetMeetingData();
        //TODO: Постараться удалить повторные запросы митингов и раписания
        isMobile ? scheduleViewModel.getMeetingsFromStartDay() : calendarViewModel.getAllMeetings();
      }, () => {
      setButtonDisabled(false);
    });
  };

  const onClose = () => {
    setModifyRecurringMeetingType(ModifyRecurringMeetingTypes.ONLY_THIS_MEETING);
    if (messageDialogViewModel.changeParams?.onCancel) {
      messageDialogViewModel.changeParams?.onCancel();
    }
    messageDialogViewModel.closeChange();
  };

  return messageDialogViewModel.changeParams.isShow
    && (
      <Portal where={document.getElementById('modalContainer')}>
        <div
          className="change-recurring-meeting-wrapper"
          onClick={messageDialogViewModel.closeChange}
          style={{ zIndex: zIndexCurrentTimeLine + 1 }}
        >
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="title">
              {`${messageDialogViewModel.changeParams.isDelete ? 'Удаление' : 'Изменение'} повторяющихся встреч`}
            </div>
            <div className="content">
              <RadioGroup
                className="group"
                onChange={(_e, type) => onCheckValue(type)}
              >
                {!messageDialogViewModel.changeParams.isIntervalChanged && (
                  <FormControlLabel
                    className="label"
                    value={ModifyRecurringMeetingTypes.ONLY_THIS_MEETING}
                    checked={checkedValue === ModifyRecurringMeetingTypes.ONLY_THIS_MEETING}
                    control={<Radio />}
                    label="Только эта встреча"
                  />
                )}
                <FormControlLabel
                  className="label"
                  value={ModifyRecurringMeetingTypes.THIS_AND_SUBSEQUENT_MEETINGS}
                  checked={checkedValue === ModifyRecurringMeetingTypes.THIS_AND_SUBSEQUENT_MEETINGS}
                  control={<Radio />}
                  label="Эта и последующие встречи"
                />
                {!messageDialogViewModel.changeParams.isDateChanged && (
                  <FormControlLabel
                    className="label"
                    value={ModifyRecurringMeetingTypes.ALL_MEETINGS}
                    checked={checkedValue === ModifyRecurringMeetingTypes.ALL_MEETINGS}
                    control={<Radio />}
                    label="Все встречи"
                  />
                )}
              </RadioGroup>
            </div>
            <div className="buttons">
              <Button
                title="Отмена"
                className="outlined"
                onClick={onClose}
                disabled={buttonDisabled}
              />
              {messageDialogViewModel.changeParams.isDelete ? (
                <Button
                  title="Удалить"
                  onClick={onDelete}
                  disabled={buttonDisabled}
                />
              ) : (
                <Button
                  title="Изменить"
                  onClick={onChange}
                  disabled={buttonDisabled}
                />
              )}
            </div>
          </div>
        </div>
      </Portal>
    );
});
